import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useTenantSettings } from "stores/tenantSettings"

import { Guid, ITranslations } from "@/types"
import { getTranslation } from "utils/translations"

export interface IConfigurationTextArticle {
    articleId: Guid
    articleDescriptionTranslations: ITranslations
    articleFeatures?: ITranslations
    assets: {
        assetId: Guid | undefined
        isFixed: boolean
        lmid: string | undefined
        serialNumber: string | undefined
        assetFeatures?: ITranslations
    }[]
}

interface IGetConfigurationTextParams {
    selectedArticles: Record<Guid, boolean>
    mergeAssets: Record<Guid, boolean>
    printFeatures: Record<Guid, boolean>
    language: string | undefined
    articles: IConfigurationTextArticle[]
}

/**
 * Builds the configuration text that the "Insert configuration text" dialog writes into a text box.
 */
export default function useConfigurationText() {
    const { t } = useTranslation()
    const { tenantPrimaryLanguageIsoCode } = useTenantSettings()

    /**
     * Renders the selected articles of a configuration as text, one block per article.
     *
     * Only articles flagged in `selectedArticles` contribute; an unselected article is skipped
     * before its block and its trailing separator are emitted, so it leaves no blank line behind.
     * Per article, `mergeAssets` suppresses the asset lines and `printFeatures` adds the feature
     * lines of the article and of its assets.
     *
     * The `<n>x` prefix counts every asset row of the article because it expresses the requested
     * quantity, while only assets with a concretely assigned machine produce an LMID line. A
     * mismatch between the two is therefore expected, not a defect.
     */
    const getConfigurationText = useCallback(({
        selectedArticles,
        mergeAssets,
        printFeatures,
        language,
        articles,
    }: IGetConfigurationTextParams) => {
        const rows: string[] = []

        articles.forEach(article => {
            if (!selectedArticles[article.articleId]) return

            rows.push(`${article.assets.length ? `${article.assets.length}x` : ''} ${getTranslation(article.articleDescriptionTranslations, language, tenantPrimaryLanguageIsoCode)}`)

            if (printFeatures[article.articleId]) {
                const tr = getTranslation(article.articleFeatures, language, tenantPrimaryLanguageIsoCode)
                if (tr) {
                    rows.push(tr)
                }
            }

            if (!mergeAssets[article.articleId]) {
                article.assets.forEach(asset => {
                    if (!asset.assetId || !asset.isFixed) return
                    rows.push([
                        asset.lmid,
                        asset.serialNumber ? `(${t(`textModal.serialNo.${language || tenantPrimaryLanguageIsoCode}`, { serialNumber: asset.serialNumber })})` : '',
                    ].filter(s => !!s).join(" "))

                    if (printFeatures[article.articleId]) {
                        const tr = getTranslation(asset.assetFeatures, language, tenantPrimaryLanguageIsoCode)
                        if (tr) {
                            rows.push(tr)
                        }
                    }
                })
            }

            rows.push("\r")

        })

        if (rows.length) {
            rows.pop()
        }

        return rows.join("\n")
    }, [t, tenantPrimaryLanguageIsoCode])

    /**
     * Renders one text per selected article, in configuration order.
     *
     * Used when every selected article gets its own invoice position: each text is built from that
     * article alone, with that article's own merge/print options applied.
     */
    const getConfigurationTextPerArticle = useCallback(({
        selectedArticles,
        mergeAssets,
        printFeatures,
        language,
        articles,
    }: IGetConfigurationTextParams) => {
        return articles
            .filter(article => selectedArticles[article.articleId])
            .map(article => ({
                articleId: article.articleId,
                text: getConfigurationText({
                    selectedArticles: { [article.articleId]: true },
                    mergeAssets,
                    printFeatures,
                    language,
                    articles: [article],
                }),
            }))
    }, [getConfigurationText])

    return {
        getConfigurationText,
        getConfigurationTextPerArticle,
    }
}
